import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gauge, GameController, BookOpen, GraduationCap, Package, Megaphone,
  ChartBar, UsersThree, Buildings, ArrowLeft, List, X, Eye, Lightning
} from '@phosphor-icons/react';
import GlobalContextSwitcher from '../components/GlobalContextSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: Gauge, pathSuffix: '' },
  { id: 'games', label: 'Games', icon: GameController, pathSuffix: '/games', permission: 'publisher.games.manage' },
  { id: 'rules', label: 'Rules', icon: BookOpen, pathSuffix: '/rules', permission: 'publisher.content.manage' },
  { id: 'tutorials', label: 'Tutorials', icon: GraduationCap, pathSuffix: '/tutorials', permission: 'publisher.tutorials.manage' },
  { id: 'editions', label: 'Editions & Expansions', icon: Package, pathSuffix: '/editions', permission: 'publisher.games.manage' },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone, pathSuffix: '/campaigns', permission: 'publisher.campaigns.manage' },
  { id: 'team', label: 'Team', icon: UsersThree, pathSuffix: '/team', permission: 'publisher.team.manage' },
  { id: 'insights', label: 'Insights', icon: ChartBar, pathSuffix: '/insights', permission: 'publisher.insights.view' },
  { id: 'profile', label: 'Profile', icon: Buildings, pathSuffix: '/profile', permission: 'publisher.profile.edit' },
  { id: 'plan', label: 'Plan & Billing', icon: Lightning, pathSuffix: '/plan' },
];

export default function PublisherLayout() {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useTenant();
  const [publisherName, setPublisherName] = useState('Publisher');
  const [publisherSlug, setPublisherSlug] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (publisherId) {
      axios.get(`${API_URL}/api/publisher-portal/${publisherId}/dashboard`, { withCredentials: true })
        .then(r => { setPublisherName(r.data.publisher_name || 'Publisher'); })
        .catch(() => {});
      axios.get(`${API_URL}/api/publisher-portal/${publisherId}/profile`, { withCredentials: true })
        .then(r => setPublisherSlug(r.data.slug || ''))
        .catch(() => {});
    }
  }, [publisherId]);

  const basePath = `/publisher/${publisherId}`;
  const isActive = (suffix) => {
    const full = basePath + suffix;
    if (suffix === '') return location.pathname === basePath || location.pathname === basePath + '/';
    return location.pathname.startsWith(full);
  };

  const visibleItems = NAV_ITEMS.filter(item => !item.permission || hasPermission(item.permission));

  return (
    <div className="min-h-screen bg-[#06060A] flex" data-testid="publisher-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#0a0a12] border-r border-white/[0.06] sticky top-0 h-screen">
        <div className="px-4 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/discover')} className="text-[#555] hover:text-white transition-colors">
              <ArrowLeft size={16} />
            </button>
            <Buildings size={20} weight="duotone" className="text-[#F59E0B]" />
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate" style={{ fontFamily: 'Unbounded' }}>
                {publisherName}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-semibold">Publisher Portal</p>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-white/[0.06]">
          <GlobalContextSwitcher variant="sidebar" />
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {visibleItems.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(basePath + item.pathSuffix)}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                isActive(item.pathSuffix)
                  ? 'text-[#F59E0B] bg-[#F59E0B]/[0.06] border-r-2 border-[#F59E0B]'
                  : 'text-[#888] hover:text-white hover:bg-white/[0.03]'
              }`}
              data-testid={`publisher-nav-${item.id}`}
            >
              <item.icon size={18} weight={isActive(item.pathSuffix) ? 'duotone' : 'regular'} />
              {item.label}
            </button>
          ))}
        </nav>

        {publisherSlug && (
          <button
            onClick={() => navigate(`/pub/${publisherSlug}`)}
            className="flex items-center gap-2 px-5 py-3 text-[#555] hover:text-white text-xs border-t border-white/[0.06] transition-colors"
          >
            <Eye size={14} /> View Public Page
          </button>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0a0a12] border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/discover')}><ArrowLeft size={18} className="text-[#555]" /></button>
            <Buildings size={18} className="text-[#F59E0B]" />
            <div>
              <p className="text-white font-semibold text-sm truncate max-w-[180px]">{publisherName}</p>
              <p className="text-[9px] uppercase tracking-wider text-[#F59E0B]">Publisher</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="text-[#777]">
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-2"
            >
              {visibleItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { navigate(basePath + item.pathSuffix); setMobileOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    isActive(item.pathSuffix)
                      ? 'text-[#F59E0B] bg-[#F59E0B]/[0.06]'
                      : 'text-[#777] hover:text-white'
                  }`}
                >
                  <item.icon size={16} weight={isActive(item.pathSuffix) ? 'duotone' : 'regular'} />
                  {item.label}
                </button>
              ))}
              <div className="mt-2 mb-1">
                <GlobalContextSwitcher variant="mobile" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <div className="lg:p-8 p-4 pt-24 lg:pt-8 max-w-6xl">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
}
